import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { api } from '@/lib/api.js';
import { useTicker } from './ticker/useTicker.js';
import './HomePage.css';

/*
 * The front door.
 *
 * One banner per thing the site does, rather than a hero announcing the
 * site's own name above a list of its pages. They are ordered by what a
 * visitor is most likely to have come for, and each one carries a piece of
 * the thing it links to — a formation, the seasons, the live ticker — so
 * the page shows rather than describes.
 */

/* A token pitch for the drafts banner — the shape of the thing rather than
 * a real squad, so it costs no request and cannot go stale. Still a legal
 * XI: 3-4-3 behind a keeper. */
const PREVIEW_ROWS = [
	['LIV'],
	['ARS', 'CHE', 'MCI'],
	['TOT', 'NEW', 'AVL', 'BOU'],
	['MUN', 'CRY', 'BHA'],
];

const SEASONS = ['2026-27','2025-26','2024-25','2023-24','2022-23',
	'2021-22','2020-21','2019-20','2018-19','2017-18','2016-17'];

function SeasonSpread() {
	return (
		<ul className="seasons" aria-hidden="true">
			{SEASONS.map((season) => (
				<li key={season}>{season}</li>
			))}
		</ul>
	);
}

function DraftPreview() {
	return (
		<div className="draftshot" aria-hidden="true">
			{PREVIEW_ROWS.map((row, index) => (
				<div className="draftshot__row" key={index}>
					{row.map((team) => (
						<img key={team} src={`/static/img/kits/${team}.png`} alt="" loading="lazy" />
					))}
				</div>
			))}
		</div>
	);
}

function FixtureRuns() {
	const { status, rows, headers } = useTicker('nutmeg');

	if (status !== 'ready' || !rows.length) {
		return (
			<div className="runs runs--placeholder" aria-hidden={status !== 'error'}>
				{status === 'error' ? 'Fixture data is unavailable right now.' : null}
			</div>
		);
	}

	const span =
		headers.length > 1
			? `${headers[0]}–${headers[headers.length - 1].replace('GW', '')}`
			: headers[0];

	return (
		<div className="runs">
			<div className="runs__head">
				<span className="eyebrow">Easiest runs · {span}</span>
				<Link className="runs__all" to="/ticker">
					Full ticker →
				</Link>
			</div>

			<ol className="runs__list">
				{rows.slice(0, 5).map((row) => (
					<li key={row.team} className="runs__row">
						<span className="runs__team">{row.team}</span>
						<span className="runs__cells">
							{row.gameweeks.map(([gw, fixtures]) => {
								const first = fixtures?.[0];
								return (
									<span
										key={gw}
										className="runs__cell"
										data-difficulty={first?.opp ? first.fdr : undefined}
										title={
											first?.opp
												? `GW${gw}: ${first.opp.toUpperCase()}`
												: `GW${gw}: blank`
										}
									>
										{first?.opp ? first.opp.toUpperCase() : '—'}
									</span>
								);
							})}
						</span>
					</li>
				))}
			</ol>
		</div>
	);
}

export default function HomePage() {
	const navigate = useNavigate();
	const [leagues, setLeagues] = useState([]);
	const [fplId, setFplId] = useState('');

	useEffect(() => {
		api
			.get('/api/leagues')
			.then((list) => setLeagues(Array.isArray(list) ? list : []))
			.catch(() => setLeagues([]));
	}, []);

	const submit = (event) => {
		event.preventDefault();
		const id = fplId.trim();
		if (id) navigate(`/manager?id=${encodeURIComponent(id)}`);
	};

	return (
		<div className="home">
			<section className="hero hero--draft">
				<div className="hero__lead">
					<p className="hero__eyebrow">New</p>
					<h1 className="hero__title">
						Build a squad,
						<br />
						share the link.
					</h1>
					<p className="hero__sub">
						Send it to your mini-league and let them tell you everything
						that&apos;s wrong with it.
					</p>
					{/* The reassurance sits with the action rather than in the
					    sentence above it: it is the objection someone has with
					    their hand already on the button, not a feature. */}
					<p className="hero__act">
						<Link className="hero__cta" to="/draft">
							Start a draft
						</Link>
						<span className="hero__note">No sign-up, for you or whoever opens it</span>
					</p>
				</div>

				<DraftPreview />
			</section>

			<section className="hero">
				<div className="hero__lead">
					{/* Dated rather than counted, so it needs no edit each August. */}
					<h2 className="hero__title">Every season since 2016-17</h2>
					<p className="hero__sub">
						Every player and every team, gameweek by gameweek.
					</p>
					<Link className="hero__cta hero__cta--quiet" to="/history">
						Browse the archive
					</Link>
				</div>

				<SeasonSpread />
			</section>

			{/* Paired from here down: these four are equals, and giving each a
			    full-width band made the page all banner and no content. Their
			    headings are h2 under the one h1 above. */}
			<div className="pair">
				<section className="hero hero--compact">
					<h2 className="hero__title hero__title--small">Who&apos;s got the best run?</h2>
					<p className="hero__sub">Fixture difficulty for the gameweeks ahead, by team.</p>
					<FixtureRuns />
				</section>

				<section className="hero hero--compact">
					<h2 className="hero__title hero__title--small">How is your season going?</h2>
					{/* The charts are the reason to type an ID in, and what makes
					    them worth reading is the lines drawn beside your own —
					    naming those is more use than listing the metrics. */}
					<p className="hero__sub">
						Rank, points and captains, plotted against{' '}
						<span className="nb">OR-1</span>, <span className="nb">OR-10K</span> and
						the average.
					</p>

					<form className="idform" onSubmit={submit}>
						<label className="idform__label" htmlFor="fpl-id">
							Your FPL ID
						</label>
						<div className="idform__row">
							<input
								id="fpl-id"
								className="idform__input numeric"
								type="number"
								inputMode="numeric"
								placeholder="1234567"
								value={fplId}
								onChange={(e) => setFplId(e.target.value)}
							/>
							<button className="idform__go" type="submit">
								See my season
							</button>
						</div>
					</form>

					{/* There is nothing to plot before GW1, and someone typing
					    their ID in over the summer should hear that here rather
					    than from an empty page. */}
					<p className="hero__note">Available once the season starts.</p>
				</section>
			</div>

			<div className="pair">
				<section className="hero hero--compact">
					<h2 className="hero__title hero__title--small">In-form managers</h2>
					<p className="hero__sub">
						The fifty most in-form managers in the Top 1K, by recent points.
					</p>
					<Link className="hero__cta hero__cta--quiet" to="/top1k">
						See the form table
					</Link>
				</section>

				<section className="hero hero--compact">
					<h2 className="hero__title hero__title--small">League archive</h2>
					<p className="hero__sub">
						Completed seasons, league by league, with every final table.
					</p>
					<Link className="hero__cta hero__cta--quiet" to="/archive">
						Open the archive
					</Link>
				</section>
			</div>

			{leagues.length > 0 && (
				<section className="panel">
					<h2 className="eyebrow">Leagues</h2>
					<ul className="leagues">
						{leagues.map((league) => (
							<li key={league}>
								<Link className="league-pill" to={`/league/${league}`}>
									{league}
								</Link>
							</li>
						))}
					</ul>
					<p className="home__note">
						To add your league, tweet{' '}
						<a href="https://twitter.com/FPL_Nutmeg">@FPL_Nutmeg</a>.
					</p>
				</section>
			)}
		</div>
	);
}
